import React, { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import findIndex from 'lodash/findIndex';
import { useDeepCompareEffect } from 'utils/hooks';
import { getToUrl, translateData } from 'utils/translation';
import PrimoTransitionBanner from 'components/ContentfulFields/PrimoTransitionBanner';
import PromotionAppliedDialog from 'components/Dialogs/PromotionAppliedDialog/Loadable';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import PromotionBanner from './PromotionBanner/Loadable';
import quickShopContent from './quickShopContent';
import { PROMOTION_REDEEM, SELECT_EQUIPMENT, SELECT_WATER } from './constants';
import CostcoPromotionBanner from './CostcoPromotionBanner';
import styles from './styles.scss';
import Header from './Header';
import { steps } from './steps';
import QuickShopPage from './QuickShopProducts';
import SubHeader from './SubHeader';
import Footer from './Footer';
import FamilyProductBundles from './FamilyProductBundles';
const stepsArray = Object.entries(steps).reduce((result, [key, item]) => {
  result.push({
    key,
    title: item.buttonText,
    costcoTitle: item.costco.buttonText,
    nextButtonText: item.nextButtonText,
    nextStepText: item.nextStepText,
  });
  return result;
}, []);

const QuickShopBasePage = ({
  content,
  loadFamilyProductBundles,
  familyProductBundles,
  addPromotion,
  areProductsFetching,
  productCategories = [],
  products,
  loadMerchandizedProducts,
  loadProductCategories,
  loadCartProducts,
  totalCount,
  promotionId,
  isPromotionLoading,
  cartItems,
  costcoAccount,
  acquisitionVariables,
  ssrReqRoute,
  match: {
    params: { page },
  },
}) => {
  const [isCostco, setIsCostco] = useState(false);
  const [waterProduct, setWaterProduct] = useState({});

  const [openRedeemedPromotion, setRedeemedPromotion] = useState(false);
  const showRedeemDialog = () => {
    setRedeemedPromotion(true);
  };

  const ref = useRef();
  const dispatch = useDispatch();
  const [section] = useState(localStorage.getItem('section'));
  useEffect(() => {
    if (window.isCostcoWater && isEmpty(costcoAccount)) {
      dispatch(push('/'));
    }
  }, []);

  useEffect(() => {
    loadProductCategories();
  }, []);

  useDeepCompareEffect(() => {
    if (!isEmpty(cartItems)) {
      loadCartProducts(cartItems);
    }
    const waterItem = cartItems.find(
      (item) => item.productType && item.productType.slug === 'water',
    );
    setWaterProduct(waterItem);
  }, [cartItems]);

  const {
    subtitle,
    title,
    primaryNote,
    notes,
    showSkipButton,
    skipButtonText,
    skipButtonLabel,
    skipButtonColor,
    costco,
  } = useMemo(() => steps[page], [page]);
  const activeStep = useMemo(
    () => findIndex(stepsArray, (step) => step.key === page),
    [page],
  );
  const categoryId = useMemo(() => {
    const category = productCategories.find(
      (cat) => cat.fields.urlName === steps[page].itemCategory,
    );
    return get(category, 'sys.id');
  }, [page, productCategories]);

  const loadFilteredProducts = (pageNum = 1, isCostcoWater) => {
    const loadProductsParams = {
      categoryId,
      brands: [],
      waterTypes: [],
      page: pageNum,
      isCostcoWater,
    };
    loadMerchandizedProducts(loadProductsParams);
  };

  useEffect(() => {
    if (categoryId) {
      setIsCostco(window.isCostcoWater);
      loadFilteredProducts(1, window.isCostcoWater);
    }
  }, [categoryId]);

  const onNext = () => {
    dispatch(push(getToUrl(`/quick-shop/${SELECT_EQUIPMENT}`, 'U35')));
    scrollToTop();
  };

  const onBack = () => {
    dispatch(push(getToUrl(`/quick-shop/${SELECT_WATER}`, 'U36')));
    scrollToTop();
  };

  const scrollToTop = () => {
    if (ref.current) {
      window.scrollTo(0, -ref.current.offsetTop);
    }
  };

  const promotionApplied = !!promotionId;

  let costcoPromotionBanner = null;
  // let costcoPromotionBannerLight = null;

  if (isCostco && activeStep === 0) {
    costcoPromotionBanner = (
      <CostcoPromotionBanner content={quickShopContent} />
    );
    // costcoPromotionBannerLight = (
    //   <CostcoPromotionBanner content={quickShopContent} lightBanner />
    // );
  }

  const disableNext = isEmpty(waterProduct);

  return (
    <Fragment>
      <Helmet title={translateData(isCostco ? 'Start Order' : 'Quick Shop')} />
      <main className={styles.quickShop} ref={ref}>
        {isCostco ? (
          costcoPromotionBanner
        ) : (
          <PromotionBanner
            isCartLoading={false}
            promotionApplied={promotionApplied}
            showRedeemDialog={showRedeemDialog}
            content={quickShopContent}
            addPromotion={addPromotion}
            page={page}
          />
        )}
        {activeStep === 0 && isCostco && <PrimoTransitionBanner />}
        <Header
          isCostco={isCostco}
          page={page}
          activeStep={activeStep}
          stepsArray={stepsArray}
          onNext={onNext}
          onBack={onBack}
          promotionApplied={promotionApplied}
          cartItems={cartItems}
          disableNext={disableNext}
          acquisitionVariables={acquisitionVariables}
          ssrReqRoute={ssrReqRoute}
        />
        {activeStep === 0 && !isCostco && (
          <FamilyProductBundles
            content={content.familyProductBundles}
            loadFamilyProductBundles={loadFamilyProductBundles}
            familyProductBundles={familyProductBundles}
            section={section}
          />
        )}
        <SubHeader
          title={title}
          subtitle={subtitle}
          primaryNote={primaryNote}
          notes={notes}
          showPromotionRedeemer={promotionApplied}
          promotionNote={quickShopContent[PROMOTION_REDEEM]}
          addPromotion={addPromotion}
          showRedeemDialog={showRedeemDialog}
          showSkipButton={showSkipButton}
          skipButtonText={skipButtonText}
          skipButtonLabel={skipButtonLabel}
          skipButtonColor={skipButtonColor}
          onNext={onNext}
          promotionApplied={promotionApplied}
          cartItems={cartItems}
          activeStep={activeStep}
          isCostco={isCostco}
          costco={costco}
        />
        {/* {costcoPromotionBannerLight} */}
        <QuickShopPage
          page={page}
          products={products}
          totalCount={totalCount}
          loadFilteredProducts={loadFilteredProducts}
          areProductsFetching={areProductsFetching}
          isCostco={isCostco}
        />
        <Header
          isCostco={isCostco}
          page={page}
          activeStep={activeStep}
          stepsArray={stepsArray}
          onNext={onNext}
          onBack={onBack}
          promotionApplied={promotionApplied}
          cartItems={cartItems}
          disableNext={disableNext}
          isPrimaryHeader={false}
        />
        <Footer
          page={page}
          onNext={onNext}
          nextStep={activeStep + 1}
          promotionApplied={promotionApplied}
          cartItems={cartItems}
          isCostco={isCostco}
          disableNext={disableNext}
        />
        <PromotionAppliedDialog
          open={openRedeemedPromotion}
          setStatus={setRedeemedPromotion}
          promotionLoading={isPromotionLoading}
        />
      </main>
    </Fragment>
  );
};

QuickShopBasePage.propTypes = {
  addPromotion: PropTypes.func.isRequired,
  loadMerchandizedProducts: PropTypes.func.isRequired,
  loadProductCategories: PropTypes.func.isRequired,
  loadCartProducts: PropTypes.func.isRequired,
  areProductsFetching: PropTypes.bool,
  productCategories: PropTypes.arrayOf(PropTypes.object),
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number,
  promotionId: PropTypes.number,
  isPromotionLoading: PropTypes.bool,
  cartItems: PropTypes.array,
  costcoAccount: PropTypes.object,
  acquisitionVariables: PropTypes.array,
  ssrReqRoute: PropTypes.object,
  match: PropTypes.shape({
    params: PropTypes.shape({
      page: PropTypes.string,
    }),
  }),
};

export default QuickShopBasePage;
